import { useLocationAddressQuery } from '../api/queries';
import type { AddressSegment } from '../types';

type Props = { af: string | null };

// Renders one side of the diff, bolding the runs that don't match the other.
function DiffLine({ label, segments }: { label: string; segments: AddressSegment[] }) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
      <span
        style={{
          flex: '0 0 auto',
          color: '#8a95a5',
          fontSize: 11,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
          minWidth: 34,
        }}
      >
        {label}
      </span>
      <span>
        {segments.map((seg, i) =>
          seg.same ? (
            <span key={i}>{seg.text}</span>
          ) : (
            <strong key={i} style={{ fontWeight: 700, color: '#dc2626' }}>
              {seg.text}
            </strong>
          ),
        )}
      </span>
    </div>
  );
}

export default function LocationAddress({ af }: Props) {
  const { data, isLoading, error } = useLocationAddressQuery(af);

  if (!af) return <span className="empty">—</span>;
  if (isLoading) return <span className="empty">Loading...</span>;
  if (error || !data) return <span className="empty">Unable to load</span>;

  const { hasZoho, hasYext, matchLevel, zoho, yext, diff } = data;

  if (!hasZoho) {
    if (hasYext) {
      return (
        <>
          <div>{yext.fullAddress}</div>
          <div
            title="No Zoho office address on file. Showing Yext address instead."
            style={{
              marginTop: 4,
              display: 'inline-block',
              padding: '2px 8px',
              background: '#eef2ff',
              color: '#4338ca',
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            from Yext
          </div>
        </>
      );
    }
    return <span className="empty">No address on file</span>;
  }

  let indicator: React.ReactNode = null;
  if (matchLevel === 'no-yext' || !hasYext) {
    indicator = (
      <div
        title="No Yext listing configured for this location"
        style={{
          marginTop: 4,
          color: '#8a95a5',
          fontSize: 11,
          fontWeight: 500,
          textAlign: 'left',
        }}
      >
        (no Yext)
      </div>
    );
  } else if (matchLevel === 'match') {
    indicator = (
      <div
        title="Yext address matches Zoho"
        style={{
          marginTop: 4,
          color: '#16a34a',
          fontSize: 12,
          fontWeight: 700,
          textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 15 }}>✔</span> Yext Matches Zoho
      </div>
    );
  } else {
    indicator = (
      <div
        title="Yext address differs from Zoho — the bolded parts are what disagree"
        style={{
          marginTop: 4,
          color: '#dc2626',
          fontSize: 12,
          fontWeight: 700,
          textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 15 }}>✗</span> Yext ≠ Zoho
      </div>
    );
  }

  // On a mismatch we list both addresses instead of the single Zoho line, so
  // the difference is visible without opening Yext.
  if (matchLevel === 'differs' && diff) {
    return (
      <>
        <div style={{ display: 'grid', gap: 2 }}>
          <DiffLine label="Zoho" segments={diff.zoho} />
          <DiffLine label="Yext" segments={diff.yext} />
        </div>
        {indicator}
      </>
    );
  }

  return (
    <>
      <div>{zoho.fullAddress}</div>
      {indicator}
    </>
  );
}
